// Robust image resolver: tries multiple sources/IDs to guarantee a visible
// image (or a placeholder as an absolute last resort).
//
// The resolution order is:
//   1. The preferred source + ID (what was randomly selected for this round).
//   2. Other IDs from the SAME source (fast — same CDN).
//   3. All IDs from the OTHER source (cross-service fallback).
//   4. Placeholder text image.
//
// Each candidate is fetched (proxy → direct URL) then tested with a quick
// <img>.onload check before we accept it.

import {
  fetchPixabayImage,
  fetchPexelsImage,
  isImageAccessible,
  getPlaceholderImageUrl,
} from './unlimitedGameImages';

// How many IDs per service to attempt before giving up on that service.
// Keeps latency bounded while still giving a solid chance.
const MAX_IDS_PER_SOURCE = 3;

/**
 * Build a priority-ordered list of { source, id } candidates.
 *
 * @param {{ pixabayIds?: number[], pexelsIds?: number[] }} imageData
 * @param {string|null} preferredSource  – 'pixabay' | 'pexels'
 * @param {number|null} preferredId
 * @returns {{ source: string, id: number }[]}
 */
function buildCandidates(imageData, preferredSource, preferredId) {
  if (!imageData) return [];

  const candidates = [];
  const seen = new Set();

  const add = (source, id) => {
    const key = `${source}-${id}`;
    if (seen.has(key)) return;
    seen.add(key);
    candidates.push({ source, id });
  };

  // 1. Preferred candidate first.
  if (preferredSource && preferredId != null) {
    add(preferredSource, preferredId);
  }

  // 2. Same-source alternatives (shuffled, capped).
  const sameSrc = preferredSource === 'pixabay'
    ? imageData.pixabayIds
    : imageData.pexelsIds;
  for (const id of shuffle(sameSrc || []).slice(0, MAX_IDS_PER_SOURCE)) {
    add(preferredSource || 'pexels', id);
  }

  // 3. Cross-service alternatives (shuffled, capped).
  const otherSource = preferredSource === 'pixabay' ? 'pexels' : 'pixabay';
  const otherIds = otherSource === 'pixabay'
    ? imageData.pixabayIds
    : imageData.pexelsIds;
  for (const id of shuffle(otherIds || []).slice(0, MAX_IDS_PER_SOURCE)) {
    add(otherSource, id);
  }

  return candidates;
}

/** Fisher-Yates shuffle (returns a new array). */
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Resolve an accessible image URL for a word, trying multiple candidates
 * across services before falling back to placeholder.
 *
 * @param {Object} opts
 * @param {string} opts.word              – the target word.
 * @param {Object} opts.wordImageMap      – full word→imageData map.
 * @param {string|null} opts.preferredSource – 'pixabay' | 'pexels' | null.
 * @param {number|null} opts.preferredId     – initially-chosen image ID.
 * @returns {Promise<{ url: string, imageSource: string|null, imageId: number|null }>}
 */
export async function resolveAccessibleImage({
  word,
  wordImageMap,
  preferredSource = null,
  preferredId = null,
}) {
  const imageData = wordImageMap?.[word?.toLowerCase()];
  const candidates = buildCandidates(imageData, preferredSource, preferredId);

  const fetchFn = (source) =>
    source === 'pixabay' ? fetchPixabayImage : fetchPexelsImage;

  for (const { source, id } of candidates) {
    try {
      const url = await fetchFn(source)(id);
      if (await isImageAccessible(url)) {
        return { url, imageSource: source, imageId: id };
      }
    } catch {
      // Move on to the next candidate.
    }
  }

  // Absolute fallback — placeholder always loads.
  return {
    url: getPlaceholderImageUrl(word),
    imageSource: null,
    imageId: null,
  };
}
